/**
 * PingChannel Module
 * Handles connection to the PingChannel for testing WebSocket connectivity
 */
class PingChannel {
  /**
   * Create a new PingChannel module
   * @param {Object} coreService - The core WebSocket service
   */
  constructor(coreService) {
    this.core = coreService;
    this.pingSubscription = null;
    this._pingConnected = false;
    this._lastPongReceived = null;
  }

  /**
   * Connect to the PingChannel
   * @returns {Object|null} The subscription object or null if connection failed
   */
  connect() {
    if (!this.core.initialized) {
      this.core.initialize();
    }
    
    // Don't create multiple ping subscriptions
    if (this.pingSubscription) {
      return this.pingSubscription;
    }
    
    console.log('Connecting to PingChannel for WebSocket testing');
    
    try {
      this.pingSubscription = this.core.cable.subscriptions.create(
        { channel: 'PingChannel' },
        {
          connected: () => {
            console.log('PingChannel connected successfully');
            this._pingConnected = true;
            
            // Send an initial ping to verify two-way communication
            this.pingSubscription.perform('ping', { timestamp: new Date().toISOString() });
          },
          
          disconnected: () => {
            console.log('PingChannel disconnected');
            this._pingConnected = false;
          },
          
          received: (data) => {
            console.log('PingChannel received message:', data);
            
            if (data.action === 'pong') {
              console.log('PingChannel: Received pong response, WebSocket is working correctly');
              this._lastPongReceived = Date.now();
            }
          },
          
          // Method to send a ping
          sendPing: function() {
            this.perform('ping', { timestamp: new Date().toISOString() });
          }
        }
      );
      
      return this.pingSubscription;
    } catch (error) {
      console.error('Error connecting to PingChannel:', error);
      return null;
    }
  }

  /**
   * Disconnect from the PingChannel
   */
  disconnect() {
    if (this.pingSubscription) {
      try {
        this.core.cable.subscriptions.remove(this.pingSubscription);
      } catch (error) {
        console.warn('Error disconnecting from PingChannel:', error);
      }
      this.pingSubscription = null;
      this._pingConnected = false;
    }
  }

  /**
   * Send a ping message
   * @returns {boolean} Whether the ping was sent successfully
   */
  sendPing() {
    if (this.pingSubscription && this._pingConnected) {
      try {
        this.pingSubscription.sendPing();
        return true;
      } catch (error) {
        console.error('Error sending ping:', error);
        return false;
      }
    }
    return false;
  }

  /**
   * Check if the PingChannel is connected
   * @returns {boolean} Whether the PingChannel is connected
   */
  isConnected() {
    return this._pingConnected;
  }

  /**
   * Get the time of the last pong received
   * @returns {number|null} The timestamp of the last pong received, or null if none received
   */
  getLastPongTime() {
    return this._lastPongReceived;
  }
}

export default PingChannel;
